import { computed, inject, Service, signal } from '@angular/core'

import { MessagesService } from '@shared/services'
import { Notification } from '../models/notification.model'
import { NotificationsApi } from './notifications-api/notifications-api'

/**
 * Estado de las notificaciones. Vive en un servicio y no en el panel para que
 * el contador del header siga funcionando con el panel cerrado.
 */
@Service()
export class NotificationsStore {
  readonly #api = inject(NotificationsApi)
  readonly #messages = inject(MessagesService)

  readonly #notifications = signal<Notification[]>([])
  readonly #loading = signal(false)

  readonly notifications = this.#notifications.asReadonly()
  readonly loading = this.#loading.asReadonly()
  readonly unread = computed(() => this.#notifications().filter(item => !item.read).length)

  async load(): Promise<void> {
    this.#loading.set(true)

    try {
      this.#notifications.set(await this.#api.getNotifications())
    } catch {
      void this.#messages.error(null, 'No se pudieron cargar las notificaciones')
    } finally {
      this.#loading.set(false)
    }
  }

  /** La pantalla se actualiza antes de escribir y se revierte si la escritura falla. */
  async markAsRead(id: string): Promise<void> {
    const previous = this.#notifications()
    if (previous.find(item => item.id === id)?.read !== false) return

    this.#notifications.set(previous.map(item => (item.id === id ? { ...item, read: true } : item)))

    try {
      await this.#api.markAsRead(id)
    } catch {
      this.#notifications.set(previous)
      void this.#messages.error(null, 'No se pudo guardar la notificación como leída')
    }
  }
}
